/**
 * Authentication Context for AdCanvas
 *
 * Provides user authentication state and methods throughout the app.
 * Uses Supabase Auth for session management.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../utils/supabase/client';
import { identifyUser, resetUser, trackEvent } from '../lib/posthog';
import { setUserContext, clearUserContext } from '../lib/sentry';

// ============================================================================
// TYPES
// ============================================================================

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isBatman: boolean;
  isBatmanResolved: boolean; // true once Batman status query completes
  isAnalyst: boolean;
  isAnalystResolved: boolean; // true once Analyst status query completes
  hasSession: boolean | null; // Fast check: null = checking, true/false = checked
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

// Check localStorage synchronously for cached session (for instant rendering)
const getOptimisticSession = (): boolean => {
  try {
    const storageKey = 'sb-pdawphmepoiqugcviqpz-auth-token';
    const cached = localStorage.getItem(storageKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if session exists and hasn't expired
      if (parsed?.expires_at) {
        const expiresAt = parsed.expires_at * 1000; // Convert to ms
        return Date.now() < expiresAt;
      }
      return !!parsed;
    }
  } catch {
    // Ignore errors, fall back to null
  }
  return false;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isBatman, setIsBatman] = useState(false);
  const [isBatmanResolved, setIsBatmanResolved] = useState(false);
  const [isAnalyst, setIsAnalyst] = useState(false);
  const [isAnalystResolved, setIsAnalystResolved] = useState(false);
  // OPTIMISTIC: Check localStorage synchronously to avoid loading screen for returning users
  const [hasSession, setHasSession] = useState<boolean | null>(() => {
    const optimistic = getOptimisticSession();
    console.log('[AuthContext] Initial hasSession:', optimistic ? true : null, '(optimistic:', optimistic, ')');
    return optimistic ? true : null; // true = cached session found, null = need to check
  });

  // Initialize auth state on mount
  useEffect(() => {
    // Timeout helper to prevent indefinite hanging
    const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
      Promise.race([
        promise,
        new Promise<T>((_, reject) =>
          setTimeout(() => reject(new Error('Auth initialization timeout')), ms)
        ),
      ]);

    // Get initial session
    const initializeAuth = async () => {
      console.log('[AuthContext] initializeAuth starting...');
      try {
        // Add 10 second timeout to prevent infinite hanging
        const { data: { session: initialSession } } = await withTimeout(
          supabase.auth.getSession(),
          10000
        );
        console.log('[AuthContext] getSession result:', initialSession ? 'HAS SESSION' : 'NO SESSION');
        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        // FAST: Set session existence immediately so UI can render
        setHasSession(!!initialSession);
        console.log('[AuthContext] hasSession set to:', !!initialSession);

        // BACKGROUND: Fetch Batman + Analyst status without blocking
        if (initialSession?.user) {
          supabase
            .from('users')
            .select('is_batman, is_analyst')
            .eq('id', initialSession.user.id)
            .single()
            .then(({ data: userData }) => {
              setIsBatman(userData?.is_batman ?? false);
              setIsBatmanResolved(true);
              setIsAnalyst(userData?.is_analyst ?? false);
              setIsAnalystResolved(true);
            })
            .catch(() => {
              setIsBatman(false);
              setIsBatmanResolved(true);
              setIsAnalyst(false);
              setIsAnalystResolved(true);
            });
        } else {
          // No user = definitely not Batman or Analyst
          setIsBatmanResolved(true);
          setIsAnalystResolved(true);
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        // Only set hasSession to false if onAuthStateChange hasn't already set it
        setHasSession(prev => {
          const newVal = prev === null ? false : prev;
          console.log('[AuthContext] Error handler: hasSession', prev, '->', newVal);
          return newVal;
        });
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
        console.log('[AuthContext] initializeAuth complete, isInitialized=true');
      }
    };

    initializeAuth();

    // Listen for auth state changes
    // IMPORTANT: NO async/await inside this callback - causes deadlock per Supabase issue #762
    // https://github.com/supabase/gotrue-js/issues/762
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('Auth state changed:', event);
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setHasSession(!!newSession);
        setIsLoading(false);

        // Track auth events and identify user
        if (newSession?.user) {
          // Identify user in PostHog and Sentry
          identifyUser(newSession.user.id, {
            email: newSession.user.email,
            created_at: newSession.user.created_at,
          });
          setUserContext(newSession.user.id, newSession.user.email);

          // Track specific auth events
          if (event === 'SIGNED_IN') {
            trackEvent('auth_login_completed', { method: 'password' });
          } else if (event === 'USER_UPDATED') {
            trackEvent('auth_user_updated');
          }
        } else {
          // User logged out - reset analytics identity
          resetUser();
          clearUserContext();

          if (event === 'SIGNED_OUT') {
            trackEvent('auth_logout_completed');
          }
        }

        // DEFER async operations with setTimeout to avoid deadlock
        if (newSession?.user) {
          setIsBatmanResolved(false); // Reset while fetching
          setIsAnalystResolved(false);
          setTimeout(async () => {
            try {
              const { data: userData } = await supabase
                .from('users')
                .select('is_batman, is_analyst')
                .eq('id', newSession.user.id)
                .single();
              setIsBatman(userData?.is_batman ?? false);
              setIsBatmanResolved(true);
              setIsAnalyst(userData?.is_analyst ?? false);
              setIsAnalystResolved(true);
            } catch {
              setIsBatman(false);
              setIsBatmanResolved(true);
              setIsAnalyst(false);
              setIsAnalystResolved(true);
            }
          }, 0);
        } else {
          setIsBatman(false);
          setIsBatmanResolved(true);
          setIsAnalyst(false);
          setIsAnalystResolved(true);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with email and password
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error as AuthError };
    }
  }, []);

  // Sign up with email and password
  const signUp = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      return { error };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error: error as AuthError };
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setIsBatman(false);
      setIsAnalyst(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, []);

  // Reset password (send email)
  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      console.error('Reset password error:', error);
      return { error: error as AuthError };
    }
  }, []);

  // Update password
  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      return { error };
    } catch (error) {
      console.error('Update password error:', error);
      return { error: error as AuthError };
    }
  }, []);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!session,
    isInitialized,
    isBatman,
    isBatmanResolved,
    isAnalyst,
    isAnalystResolved,
    hasSession,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
